const ENVIRONMENTS = ["production", "staging", "development"] as const;

type Environment = (typeof ENVIRONMENTS)[number];

/**
 * Build time environment variables.
 *
 * Make sure to also update:
 * - ui/src/vite-env.d.ts
 * - _deploy/staging/ui.cloudbuild.yaml
 * - _deploy/production/ui.cloudbuild.yaml
 * - ui/Dockerfile
 */
const ENVIRONMENT_VARIABLES = {
  AMPLITUDE_API_KEY: "VITE_AMPLITUDE_API_KEY",
  DOMAIN_NAME: "VITE_DOMAIN_NAME",
  ENVIRONMENT: "VITE_ENVIRONMENT",
  TIPTAP_SERVER_ID: "VITE_TIPTAP_SERVER_ID",
} as const;

type EnvironmentVariable = keyof typeof ENVIRONMENT_VARIABLES;

export function getEnvironmentVariable(
  name: EnvironmentVariable,
  required?: true,
): string;
export function getEnvironmentVariable(
  name: EnvironmentVariable,
  required: false,
): string | null;
export function getEnvironmentVariable(
  name: EnvironmentVariable,
  required = true,
): string | null {
  const value = import.meta.env[ENVIRONMENT_VARIABLES[name]];

  if (typeof value !== "string" || value.length === 0) {
    if (required) {
      throw new Error(`Missing environment variable: ${name}`);
    }
    console.error(`Missing environment variable: ${name}`);
    return null;
  }

  return value;
}

export const getEnvironment = (): Environment => {
  const value = getEnvironmentVariable("ENVIRONMENT");

  if (!(ENVIRONMENTS as readonly string[]).includes(value)) {
    throw new Error(
      `Invalid ENVIRONMENT: ${value}. Expected one of: ${ENVIRONMENTS.join(", ")}`,
    );
  }

  return value as Environment;
};
